/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { styles } from '../ReportsScreen.styles';
import { spacing } from '../../../shared/theme/spacing';
import { ReportsDenseListCard } from './ReportsDenseListCard';
import { ReportsBlockHeading } from './ReportsBlockHeading';
import { ReportsLoadErrorRow } from './ReportsLoadErrorRow';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';
import { supabase } from '../../../shared/supabase/supabaseClient';
import { ticketColors } from '../../../shared/theme/ticketColors';

type FunnelRow = {
  orders_created: number;
  paid: number;
  cancelled: number;
  conversion_pct: number;
};

export function OnlineFunnelSection({ from, to }: { from: string; to: string }) {
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const [row, setRow] = useState<FunnelRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!currentBranchId) return;
    setLoading(true);
    setLoadError(false);
    Promise.resolve(
      supabase
        .rpc('reports_online_order_funnel', {
          p_branch_id: currentBranchId,
          p_from: from,
          p_to: to,
        })
        .then(({ data, error }) => {
          if (error) {
            console.warn('OnlineFunnelSection', error);
            setRow(null);
            setLoadError(true);
            return;
          }
          const first = Array.isArray(data) ? data[0] : data;
          if (!first || typeof first !== 'object') {
            setRow(null);
            return;
          }
          const r = first as Record<string, unknown>;
          setRow({
            orders_created: Number(r.orders_created ?? 0),
            paid: Number(r.paid ?? 0),
            cancelled: Number(r.cancelled ?? 0),
            conversion_pct: Number(r.conversion_pct ?? 0),
          });
        }),
    ).finally(() => setLoading(false));
  }, [currentBranchId, from, to, reloadTick]);

  if (!currentBranchId) return null;

  const created = row?.orders_created ?? 0;

  return (
    <View style={{ marginTop: spacing.md }}>
      <ReportsBlockHeading label="Conversión pedido→pago" />
      <ReportsDenseListCard>
        {loading ? (
          <Text style={styles.caption}>Cargando…</Text>
        ) : loadError ? (
          <ReportsLoadErrorRow
            message="No se pudo cargar el embudo de pedidos."
            onRetry={() => setReloadTick((t) => t + 1)}
          />
        ) : created > 0 && row ? (
          <>
            <View style={styles.reportsDenseListFieldRow}>
              <Text style={styles.reportsDenseListFieldLabel}>Pedidos creados</Text>
              <Text style={styles.reportsDenseListFieldValue}>{row.orders_created}</Text>
            </View>
            <View style={styles.reportsDenseListFieldRow}>
              <Text style={styles.reportsDenseListFieldLabel}>Pagados</Text>
              <Text style={[styles.reportsDenseListFieldValue, { color: ticketColors.gold }]}>
                {row.paid}
              </Text>
            </View>
            <View style={styles.reportsDenseListFieldRow}>
              <Text style={styles.reportsDenseListFieldLabel}>Cancelados</Text>
              <Text style={styles.reportsDenseListFieldValue}>{row.cancelled}</Text>
            </View>
            <View
              style={[
                styles.reportsDenseListFieldRow,
                { borderTopWidth: 1, borderTopColor: '#E0E0E0', paddingTop: spacing.xs, marginTop: spacing.xs },
              ]}
            >
              <Text style={styles.reportsDenseListFieldLabel}>Conversión</Text>
              <Text style={styles.reportsDenseListFieldValue}>
                {row.conversion_pct.toLocaleString('es-AR', { maximumFractionDigits: 1 })}%
              </Text>
            </View>
          </>
        ) : (
          <Text style={styles.caption}>Sin actividad de pedidos por internet en el período.</Text>
        )}
      </ReportsDenseListCard>
    </View>
  );
}
